import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Lock } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import { API } from "@/lib/content";

const STORAGE_KEY = "amm_admin_key";

const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState(null);

  const load = async (k) => {
    try {
      const res = await axios.get(`${API}/henvendelser`, { headers: { "X-Admin-Key": k } });
      setItems(res.data);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, k);
    } catch {
      setAuthed(false);
      sessionStorage.removeItem(STORAGE_KEY);
      toast.error("Ugyldig adgangsnøgle");
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pt-36 pb-24 max-w-[1100px] mx-auto px-5 md:px-10 min-h-screen" data-testid="admin-page">
      <Seo title="Admin — Henvendelser | Al Maler Mester ApS" description="Intern oversigt over henvendelser." />
      {!authed ? (
        <div className="max-w-md">
          <p className="text-[10px] uppercase tracking-[0.25em] text-signal font-semibold mb-4">Intern side</p>
          <h1 className="font-cabinet text-3xl font-bold tracking-tighter">Henvendelser</h1>
          <p className="mt-3 text-sm text-soot">Indtast adgangsnøglen for at se modtagne tilbudshenvendelser.</p>
          <form
            className="mt-8 flex gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
          >
            <div className="flex items-center gap-2 border-b border-ink/20 focus-within:border-signal flex-1 transition-colors">
              <Lock size={16} className="text-soot" />
              <input
                type="password"
                data-testid="admin-key-input"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Adgangsnøgle"
                className="w-full bg-transparent outline-none py-3 text-sm"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-button"
              className="bg-ink text-bone text-sm font-semibold px-6 hover:bg-signal transition-colors duration-300"
            >
              Åbn
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-signal font-semibold mb-2">Intern side</p>
              <h1 className="font-cabinet text-3xl font-bold tracking-tighter">
                Henvendelser <span className="font-editorial italic font-medium text-signal">({items?.length ?? 0})</span>
              </h1>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                sessionStorage.removeItem(STORAGE_KEY);
                setAuthed(false);
                setKey("");
                setItems(null);
              }}
              className="text-xs font-semibold border-b border-ink pb-0.5 hover:text-signal hover:border-signal transition-colors"
            >
              Log ud
            </button>
          </div>
          {items?.length === 0 && (
            <p className="text-soot text-sm" data-testid="admin-empty">Ingen henvendelser endnu.</p>
          )}
          <div className="space-y-4">
            {items?.map((h, i) => (
              <article key={h.id} data-testid={`admin-inquiry-${i}`} className="bg-white border border-ink/10 p-6 md:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-cabinet text-lg font-bold tracking-tight">{h.virksomhedsnavn}</h2>
                    <p className="text-xs text-soot mt-1">
                      {new Date(h.oprettet).toLocaleString("da-DK")} · {h.opgavetype} · Opstart: {h.opstart}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-bone border border-ink/10 px-3 py-1.5">
                    {h.cvr ? `CVR ${h.cvr}` : "CVR ikke angivet"}
                  </span>
                </div>
                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <div><dt className="text-soot text-xs">Kontaktperson</dt><dd className="font-medium">{h.kontaktperson}</dd></div>
                  <div><dt className="text-soot text-xs">Telefon</dt><dd className="font-medium"><a className="hover:text-signal" href={`tel:${h.telefon}`}>{h.telefon}</a></dd></div>
                  <div><dt className="text-soot text-xs">E-mail</dt><dd className="font-medium"><a className="hover:text-signal" href={`mailto:${h.email}`}>{h.email}</a></dd></div>
                  <div><dt className="text-soot text-xs">Projektadresse</dt><dd className="font-medium">{h.projektadresse}</dd></div>
                </dl>
                <p className="mt-5 text-sm text-soot leading-relaxed whitespace-pre-line border-t border-ink/10 pt-4">{h.beskrivelse}</p>
                {h.filer?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {h.filer.map((f, j) => (
                      <a
                        key={j}
                        data-testid={`admin-file-${i}-${j}`}
                        href={`${API}/uploads/${f.sti}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs font-medium border border-ink/15 px-3 py-2 hover:border-signal hover:text-signal transition-colors"
                      >
                        <Download size={13} /> {f.originalt_navn}
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;
